import type { FieldValues } from "react-hook-form";
import type { InputWithLabelsExposedProps } from "../_components/InputWithLabels/types";
import type {
  CommonComponentProps,
  ControlledInputComponentProps,
  InputComponentProps,
  SituationalComponentProps,
} from "../../utils/component-props";
import { InputTypes } from "../../Form/FormHelpers/types";

export const DynamicDataTypeInputDataTypes = {
  STRING: InputTypes.STRING,
  NUMBER: InputTypes.NUMBER,
  DATE: InputTypes.DATE,
  PICK_LIST: InputTypes.PICK_LIST,
  MULTI_PICK_LIST: InputTypes.MULTI_PICK_LIST,
} as const;

export type DynamicDataTypeInputDataTypesType =
  (typeof DynamicDataTypeInputDataTypes)[keyof typeof DynamicDataTypeInputDataTypes];

export const DynamicDataTypeInputModes = {
  VARIABLE: "variable",
  CUSTOM_ANSWER: "custom_answer",
  EXISTING_DEFAULT_ANSWER: "existing_default_answer",
  BLANK: "blank",
} as const;

export type DynamicDataTypeInputModesType =
  (typeof DynamicDataTypeInputModes)[keyof typeof DynamicDataTypeInputModes];

/**
 * "date-fns"-compatible types that are supported by DynamicDataTypeInput
 */
export type DynamicDataTypeInputSupportedDateInputFormats =
  | "yyyy"
  | "yyyyMM"
  | "yyyy-MM-dd";

export type DynamicDataTypeInputProps = Partial<CommonComponentProps> &
  Pick<
    InputComponentProps,
    | "name"
    | "id"
    | "className"
    | "style"
    | "aria-invalid"
    | "aria-label"
    | "labelledById"
  > &
  Pick<
    SituationalComponentProps,
    "error" | "invalidMessage" | "isInline" | "isFluid"
  > & {
    /** The data type of the input. */
    dataType: DynamicDataTypeInputDataTypesType;
    /** The value of the input. */
    value?: any;
    /** The function that is called when the value of the input changes. */
    onChange?: (value: any) => void;
    /** The default value of the input. */
    defaultValue?: any;
    /** The variables that can be selected in the Variable mode. */
    variables?: Array<{ label: string; value: string }>;
    /** The options that are available to the input if the data type is pick list or multi pick list. */
    dropdownOptions?: any[];
    /** The format of the date input if the data type is date. */
    dateFormat?: DynamicDataTypeInputSupportedDateInputFormats;
    /** The mode of the input. */
    mode: DynamicDataTypeInputModesType;
    /** The function that is called when the mode of the input changes. */
    onModeChange: (mode: DynamicDataTypeInputModesType) => void;
  };

export type ControlledDynamicDataTypeInputProps<T extends FieldValues> =
  ControlledInputComponentProps<T, DynamicDataTypeInputProps> &
    InputWithLabelsExposedProps;
